<template>
  <div class="m-expansion" :class="expansionClasses" :data-testid="panelTestId">
    <Accordion v-model:value="activeIndex">
      <AccordionPanel :value="panelId">
        <AccordionHeader
          v-if="!hideHeader"
          :class="{ 'expansion-header-wrap--draggable': draggable }"
          :data-overlay-drag-handle="draggable ? true : undefined"
        >
          <div
            class="expansion-header"
            :class="{ 'expansion-header--draggable': draggable }"
            :data-overlay-drag-handle="draggable ? true : undefined"
          >
            <h3 class="expansion-title">{{ title }}</h3>
          </div>
        </AccordionHeader>
        <AccordionContent>
          <div class="expansion-content">
            <slot />
          </div>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>
  </div>
</template>

<script setup lang="ts">
import Accordion from 'primevue/accordion'
import AccordionContent from 'primevue/accordioncontent'
import AccordionHeader from 'primevue/accordionheader'
import AccordionPanel from 'primevue/accordionpanel'
import { computed } from 'vue'
import { buildStableTestId } from '@/common/utils/testId'

interface Props {
  title?: string
  modelValue?: number
  hideHeader?: boolean
  panelId?: number | string
  pGroupStyle?: boolean
  draggable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  modelValue: 0,
  panelId: 0,
  hideHeader: false,
  pGroupStyle: false,
  draggable: false,
})

const emit = defineEmits<{
  'update:modelValue': [number | boolean]
}>()

const activeIndex = computed({
  get: () => {
    if (typeof props.modelValue === 'boolean') {
      return props.modelValue ? 0 : -1
    }
    return props.modelValue
  },
  set: (value) => {
    if (typeof props.modelValue === 'boolean') {
      emit('update:modelValue', value === 0)
    } else {
      emit('update:modelValue', value)
    }
  },
})

const panelTestId = computed(() => buildStableTestId('panel', [props.panelId]))

const expansionClasses = computed(() => [
  'm-expansion',
  {
    'm-expansion--no-header': props.hideHeader,
    'm-expansion--expanded': activeIndex.value === 0,
    'm-expansion--pgroup': props.pGroupStyle,
  },
])
</script>

<style lang="scss">
.m-expansion {
  border-bottom: 1px solid #e5e7eb;
  width: 100%;

  .p-accordioncontent {
    --p-accordion-content-background: #ffffff !important;
    --p-accordion-content-padding: 1em;
  }

  :deep(.p-accordion) {
    .p-accordionheader,
    .p-accordion-header .p-accordion-header-link {
      padding: 1rem;
      border: none;
      color: #374151;
    }

    .expansion-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      &--draggable {
        cursor: move;
        touch-action: none;
        user-select: none;
      }

      .expansion-title {
        margin: 0;
        font-size: 1rem;
        font-weight: 500;
      }
    }

    .p-accordioncontent-content,
    .p-accordion-content {
      padding: 0 1rem 1rem 1rem;
      border: none;
    }
  }

  &--pgroup {
    border-bottom: none;

    :deep(.p-accordion-header .p-accordion-header-link),
    :deep(.p-accordionheader) {
      background: linear-gradient(180deg, #f4f4f4 0%, #d1d1d1 100%);
      border: 1px solid #e0e0e0;
    }
  }

  :deep(.expansion-header-wrap--draggable) {
    cursor: move;
  }

  &--no-header {
    :deep(.p-accordion-header) {
      display: none;
    }

    :deep(.p-accordion-content) {
      padding: 1rem;
    }
  }
}
</style>
